import React from "react";
import { Image, Text, View } from "@react-pdf/renderer";
import { PDFRiskIndicator, pdfStyles } from "@repo/print-pdf/shared";
import type { PrintDataFields } from "../../print/hooks/use-print-data";

/** Contenu du « Diagnostic sanitaire » : tableau par partie de l'arbre, état global et dates. */
export const DiagnosticSanitaireBlock: React.FC<{ data: PrintDataFields }> = ({
  data,
}) => (
  <>
    <View style={pdfStyles.table}>
      <View style={pdfStyles.tableHeader}>
        <Text style={[pdfStyles.tableHeaderCell, { flex: 1 }]}></Text>
        <Text style={[pdfStyles.tableHeaderCell, { flex: 2 }]}>
          Défauts mécaniques
        </Text>
        <Text style={[pdfStyles.tableHeaderCell, { flex: 2 }]}>
          Préconisations de travaux
        </Text>
        <Text
          style={[pdfStyles.tableHeaderCell, { flex: 1, borderRight: "none" }]}
        >
          Risque de rupture
        </Text>
      </View>
      <View style={pdfStyles.tableRow}>
        <Text
          style={[
            pdfStyles.tableCell,
            { flex: 1, fontWeight: "bold", fontSize: 8, color: "#4E5155" },
          ]}
        >
          Système racinaire
        </Text>
        <Text
          style={[pdfStyles.tableCell, { flex: 2, fontSize: 8, padding: 4 }]}
        >
          {data.defautsMecaniques.collet}
        </Text>
        <Text
          style={[pdfStyles.tableCell, { flex: 2, fontSize: 8, padding: 4 }]}
        >
          {data.travaux.travaux_collet}
        </Text>
        <View
          style={[
            pdfStyles.tableCellLast,
            {
              flex: 1,
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 5,
              padding: 8,
            },
          ]}
        >
          <PDFRiskIndicator
            risk={data.risque.collet}
            showLabel={true}
            size="default"
          />
        </View>
      </View>
      <View style={pdfStyles.tableRow}>
        <Text
          style={[
            pdfStyles.tableCell,
            { flex: 1, fontWeight: "bold", fontSize: 8, color: "#4E5155" },
          ]}
        >
          Tronc
        </Text>
        <Text
          style={[pdfStyles.tableCell, { flex: 2, fontSize: 8, padding: 4 }]}
        >
          {data.defautsMecaniques.tronc}
        </Text>
        <Text
          style={[pdfStyles.tableCell, { flex: 2, fontSize: 8, padding: 4 }]}
        >
          {data.travaux.travaux_tronc}
        </Text>
        <View
          style={[
            pdfStyles.tableCellLast,
            {
              flex: 1,
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 5,
              padding: 8,
            },
          ]}
        >
          <PDFRiskIndicator
            risk={data.risque.tronc}
            showLabel={true}
            size="default"
          />
        </View>
      </View>
      <View style={pdfStyles.tableRow}>
        <Text
          style={[
            pdfStyles.tableCell,
            { flex: 1, fontWeight: "bold", fontSize: 8, color: "#4E5155" },
          ]}
        >
          Houppier
        </Text>
        <Text
          style={[pdfStyles.tableCell, { flex: 2, fontSize: 8, padding: 4 }]}
        >
          {data.defautsMecaniques.houppier}
        </Text>
        <Text
          style={[pdfStyles.tableCell, { flex: 2, fontSize: 8, padding: 4 }]}
        >
          {data.travaux.travaux_houppier}
        </Text>
        <View
          style={[
            pdfStyles.tableCellLast,
            {
              flex: 1,
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 5,
              padding: 8,
            },
          ]}
        >
          <PDFRiskIndicator
            risk={data.risque.houppier}
            showLabel={true}
            size="default"
          />
        </View>
      </View>
    </View>

    {/* État sanitaire global and Date prochaine visite */}
    <View style={pdfStyles.twoColGrid}>
      <View style={{ flex: 1 }}>
        <View style={pdfStyles.stateTable}>
          <View style={{ flexDirection: "row", border: "1pt solid #9ca3af" }}>
            <View style={{ flex: 1, width: 50 }}>
              <Text style={[pdfStyles.stateTableCellBold]}>
                Etat sanitaire global
              </Text>
            </View>
            <View style={{ flex: 1, width: 100 }}>
              <View style={[pdfStyles.stateTableCellValue, { width: "100%" }]}>
                <Text
                  style={{
                    fontSize: 8,
                    textTransform: "capitalize",
                    flexWrap: "wrap",
                  }}
                >
                  {data.etatSanitaire.general?.trim()}
                </Text>
              </View>
            </View>
          </View>
          <View
            style={{
              flexDirection: "row",
              border: "1pt solid #9ca3af",
              borderTop: "none",
            }}
          >
            <View style={{ flex: 1 }}>
              <Text
                style={[pdfStyles.stateTableCellBold, { borderBottom: "none" }]}
              >
                Risque de rupture général
              </Text>
            </View>
            <View style={{ flex: 1 }}>
              <View
                style={[
                  pdfStyles.stateTableCellValueLast,
                  {
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 5,
                  },
                ]}
              >
                <PDFRiskIndicator
                  risk={data.risque.general}
                  showLabel={true}
                  size="default"
                />
              </View>
            </View>
          </View>
        </View>
      </View>
      <View style={{ flex: 1 }}>
        <View
          style={[
            pdfStyles.stateTable,
            {
              border: "1pt solid #9ca3af",
              borderBottom: "none",
              borderLeft: "none",
              borderRight: "none",
            },
          ]}
        >
          <View
            style={{
              flexDirection: "row",
              border: "1pt solid #9ca3af",
              borderTop: "none",
            }}
          >
            <View style={{ flex: 1 }}>
              <Text style={pdfStyles.stateTableCellBold}>
                Date d'intervention
              </Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={pdfStyles.stateTableCellValue}>
                {data.travaux.date_travaux}
              </Text>
            </View>
          </View>
          <View style={{ flexDirection: "row", border: "1pt solid #9ca3af" }}>
            <View style={{ flex: 1 }}>
              <Text style={pdfStyles.stateTableCellBold}>
                Date de prochaine visite
              </Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={pdfStyles.stateTableCellValue}>
                {data.travaux.date_pro_visite}
              </Text>
            </View>
          </View>

          <View
            style={{
              flexDirection: "row",
              border: "1pt solid #9ca3af",
              borderTop: "none",
            }}
          >
            <View style={{ flex: 1 }}>
              <Text
                style={[pdfStyles.stateTableCellBold, { borderBottom: "none" }]}
              >
                DT/DICT requis
              </Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={pdfStyles.stateTableCellValueLast}>
                {data.dictRequired}
              </Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  </>
);
